import { buildURL } from "app/client/lib/urlUtils";
import { urlState } from "app/client/models/gristUrlState";
import { LoginAPIImpl } from "app/client/ui/LoginAPI";
import * as css from "app/client/ui/LoginPagesCss";
import { buildRegistrationForm, buildVerifyEmailForm } from "app/client/ui/SignupForms";
import { colors, mediaMedium, theme } from "app/client/ui2018/cssVars";
import { cssLink } from "app/client/ui2018/links";
import { loadingDots } from "app/client/ui2018/loaders";
import { getPageTitleSuffix } from "app/common/gristUrls";
import { getGristConfig } from "app/common/urlUtils";

import { Computed, Disposable, dom, IDisposableOwner, keyframes, makeTestId, Observable, styled } from "grainjs";

const testId = makeTestId("test-sp-");

export class SignupPage extends Disposable {
  private _currentStep: Observable<"register" | "verify-email"> = Observable.create(this, "register");
  private _email = Observable.create(this, "");
  private _assistantPrompt = Computed.create(
    this,
    urlState().state,
    (_use, s) => s.params?.assistantPrompt,
  );

  private _loginAPI = new LoginAPIImpl();

  public buildDom() {
    const suffix = getPageTitleSuffix(getGristConfig());
    document.title = `Sign up${suffix}`;

    return css.pageContainer(
      cssSignupPage(
        this._buildSidebar(),
        this._buildSignupForms(),
      ),
    );
  }

  private _buildSidebar() {
    return cssSidebar(
      cssSidebarBackground(
        cssGrid(
          cssGrid.cls("-animated", use => !!use(this._assistantPrompt)),
        ),
      ),
      dom.domComputed(this._assistantPrompt, (prompt) => {
        if (prompt) {
          return this._buildAssistantSidebarContent(prompt);
        }
        else {
          return [
            cssSidebarHeading("The world deserves a better tool than spreadsheets"),
            cssFeatureList(
              cssIconAndText(cssUnify(), cssFeatureText("Unify your data")),
              cssIconAndText(cssStreamline(), cssFeatureText("Streamline your data")),
              cssIconAndText(cssVisualize(), cssFeatureText("Visualize your data")),
            ),
          ];
        }
      }),
      cssGetStarted(
        cssGetStartedImg({ src: "img/get-started.png" }),
      ),
      testId("sidebar"),
    );
  }

  private _buildAssistantSidebarContent(prompt: string) {
    const showMessageIndex = Observable.create(null, 0);

    const showNextMessage = () =>
      showMessageIndex.set(showMessageIndex.get() + 1);

    const buildMessage = (
      index: number,
      sender: "user" | "assistant",
      message: string,
      options: { delayMs?: number } = {},
    ) =>
      dom.maybe(
        use => use(showMessageIndex) >= index,
        () =>
          dom.create(buildConversationMessage, sender, message, {
            ...options,
            onFinish: showNextMessage,
          }),
      );

    return cssAssistantSidebarContent(
      dom.autoDispose(showMessageIndex),
      buildMessage(0, "user", prompt),
      buildMessage(1, "assistant", "Getting your document ready...", {
        delayMs: 1500,
      }),
      buildMessage(2, "assistant", "Almost there! Finish signing up to continue.", {
        delayMs: 1500,
      }),
    );
  }

  private _buildSignupForms() {
    return cssSignupFormWrap(cssSignupForm(
      dom.domComputed(this._currentStep, (currentStep) => {
        switch (currentStep) {
          case "register": {
            return dom.create(buildRegistrationForm, {
              loginAPI: this._loginAPI,
              email: this._email,
              onSuccess: () => this._currentStep.set("verify-email"),
            });
          }
          case "verify-email": {
            return dom.create(buildVerifyEmailForm, {
              loginAPI: this._loginAPI,
              email: this._email,
              headingText: "Almost there!",
              footer: () => css.text(
                "Already have an account? ",
                cssLink("Sign in",
                  { href: buildURL("/login").href },
                  testId("sign-in"),
                ),
              ),
            });
          }
        }
      }),
    ));
  }
}

function buildConversationMessage(
  owner: IDisposableOwner,
  sender: "user" | "assistant",
  message: string,
  options: {
    delayMs?: number;
    onFinish?: () => void;
  } = {},
) {
  const { delayMs = 500, onFinish } = options;

  const visible = Observable.create(owner, false);
  const text = Observable.create(owner, "");

  async function showAndTypeMessage() {
    visible.set(true);
    await new Promise(r => setTimeout(r, delayMs));
    typeMessage();
  }

  function typeMessage() {
    let index = 0;
    const interval = setInterval(() => {
      if (index >= message.length) {
        clearInterval(interval);
        onFinish?.();
        return;
      }

      text.set(message.slice(0, ++index));
    }, 25);
  }

  if (sender === "user") {
    return cssUserMessage(
      cssUserMessage.cls("-visible", visible),
      cssMessageBubble(
        dom.text(text),
        testId("message-bubble"),
      ),
      cssAvatarAndName(
        cssUserAvatar("😎"),
        "You",
      ),
      () => { setTimeout(showAndTypeMessage, 10); },
    );
  }
  else {
    return cssAssistantMessage(
      cssAssistantMessage.cls("-visible", visible),
      cssAvatarAndName(
        cssAssistantAvatar(),
        "Assistant",
      ),
      cssMessageBubble(
        dom.domComputed(use => use(text) || cssLoadingDots()),
        testId("message-bubble"),
      ),
      () => { setTimeout(showAndTypeMessage, 10); },
    );
  }
}

const cssSignupPage = styled("div", `
  display: flex;
  min-height: 100%;
  background-color: ${theme.loginPageBg};
`);

const cssMoveGrid = keyframes(`
  0% {
    background-position: 0 0, 0 0;
  }
  100% {
    background-position: 240px 120px, 240px 120px;
  }
`);

const cssSidebar = styled("div", `
  width: 460px;
  color: ${colors.light};
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 0;

  @media ${mediaMedium} {
    & {
      display: none;
    }
  }
`);

const cssSidebarBackground = styled("div", `
  position: absolute;
  inset: 0;
  overflow: hidden;
  z-index: -1;
`);

const cssGrid = styled("div", `
  width: 100%;
  height: 100%;
  background-color: ${colors.lightGreen};
  background-image:
    linear-gradient(to bottom, rgb(41, 185, 131) 32px, transparent 32px),
    linear-gradient(to right, rgb(41, 185, 131) 32px, transparent 32px);
  background-size: 240px 120px;
  background-position: 0 0, 40%;

  &-animated {
    width: 200%;
    height: 200%;
    animation: ${cssMoveGrid} 10s linear infinite;
    filter: blur(4px);
  }
`);

const cssFeatureList = styled("div", `
  display: flex;
  flex-direction: column;
  margin: 32px 64px 32px 64px;
  gap: 16px;
`);

const cssIconAndText = styled("div", `
  display: flex;
  align-items: center;
  gap: 16px;
`);

const cssGetStarted = styled("div", `
  margin: auto -77px 0 37px;
  width: 500px;
  height: 350px;
  overflow: hidden;
`);

const cssGetStartedImg = styled("img", `
  display: block;
  width: 500px;
  height: auto;
`);

const cssSidebarHeading = styled("div", `
  line-height: 32px;
  margin: 76px 64px 0px 64px;
  font-size: 24px;
  font-weight: 500;
`);

const cssSignupFormWrap = styled("div", `
  flex: 1 1 auto;
  display: flex;
  justify-content: center;
  padding: 0px 80px;

  @media ${mediaMedium} {
    & {
      padding: 0px;
    }
  }
`);

const cssSignupForm = styled("div", `
  max-width: 464px;
  width: 100%;
  margin: 60px 20px 33px 20px;

  @media ${mediaMedium} {
    & {
      margin-top: 25px;
    }
  }
`);

const cssImage = styled(css.image, `
  width: 40px;
  height: 40px;
`);

const cssUnify = styled(cssImage, `
  background-image: var(--icon-LoginUnify);
`);

const cssStreamline = styled(cssImage, `
  background-image: var(--icon-LoginStreamline);
`);

const cssVisualize = styled(cssImage, `
  background-image: var(--icon-LoginVisualize);
`);

const cssFeatureText = styled("div", `
  font-weight: 400;
  line-height: 24px;
  font-size: 18px;
`);

const cssAssistantSidebarContent = styled("div", `
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 64px;
`);

const cssMessage = styled("div", `
  display: flex;
  gap: 16px;
  align-items: start;
  opacity: 0;
  transition: all 0.5s ease;
`);

const cssUserMessage = styled(cssMessage, `
  transform: translateX(16px);

  &-visible {
    opacity: 1;
    transform: translateX(0);
  }
`);

const cssAssistantMessage = styled(cssMessage, `
  transform: translateX(-16px);

  &-visible {
    opacity: 1;
    transform: translateX(0);
  }
`);

const cssAvatarAndName = styled("div", `
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`);

const cssAvatar = styled("div", `
  height: 40px;
  width: 40px;
  border-radius: 50%;
  background-color: white;
`);

const cssUserAvatar = styled(cssAvatar, `
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
`);

const cssAssistantAvatar = styled(cssAvatar, `
  background-image: var(--icon-GristLogo);
  background-size: 22px 22px;
  background-repeat: no-repeat;
  background-position: center;
`);

const cssMessageBubble = styled("div", `
  background-color: white;
  color: black;
  width: 300px;
  min-height: 39px;
  padding: 12px;
  border-radius: 12px;
`);

const cssLoadingDots = styled(loadingDots, `
  --dot-size: 5px;
  align-items: center;
`);
